import type { UITask, TaskPriority } from '@repo/common/types/Task';
import { TaskStatus } from '@repo/common/types/Task';
import type { Section, SectionWithMetadata, SectionConfig } from '@repo/common/types/FilterSet';
import { formatFullDate, formatTime } from '@repo/toolslib/src/utils/component.util';
import type { Readable } from 'svelte/store';
import { derived } from 'svelte/store';
import { FilteredType } from './base';
import { formatLocalDateOnly } from '@repo/toolslib/src/utils/date.util';

// Extended UITask that satisfies FilteredItemType constraint
type ExtendedUITask = UITask & { tags: string[] };
const plannedForToday = "Today's suggestions";

// Mapped type for home items (tasks)
export interface HomeItemType {
  id: string;
  title: string;
  time: string;
  fullDate: string;
  status: TaskStatus;
  priority?: TaskPriority;
  metadata?: string;
  workspaceId?: string;
  transcriptId?: string;
  updatedAt: Date;
  isDeleting?: boolean;
  isFavorite?: boolean;
}

// Concrete implementation for Home (tasks grouped by status)
export class FilteredHome extends FilteredType<ExtendedUITask, HomeItemType> {
  constructor(items: Readable<UITask[]>, initialLimit: number = 50) {
    // Extend UITask items with missing properties to satisfy FilteredItemType constraint
    const extendedItems = derived(items, ($items) =>
      $items.map((item) => ({ ...item, tags: [] }))
    );
    super(extendedItems, initialLimit);

    // Override the default datePreset filter - home items should show all tasks by default
    this.filterDatePresetValues.set([]);

    // Initialize section states with defaults
    this.sectionStates.set(new Map([
      ['prioritized-today', { collapsed: false, displayLimit: 3 }],
      ['in-progress', { collapsed: false, displayLimit: 3 }],
      ['to-do', { collapsed: false, displayLimit: 3 }]
    ]));
  }

  protected sortItems(items: ExtendedUITask[]): ExtendedUITask[] {
    // Sort by priority (high to low) and then by due date (earliest first)
    return [...items].sort((a, b) => {
      // Priority order map
      const priorityOrder = { URGENT: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
      const priorityA = a.priority ? (priorityOrder[a.priority] ?? 0) : 0;
      const priorityB = b.priority ? (priorityOrder[b.priority] ?? 0) : 0;

      if (priorityB !== priorityA) {
        return priorityB - priorityA;
      }

      // If priorities are equal, sort by due date
      if (a.dueDate && b.dueDate) {
        const dueDateA = new Date(a.dueDate).getTime();
        const dueDateB = new Date(b.dueDate).getTime();
        return dueDateA - dueDateB;
      }

      // Tasks with due dates come before tasks without
      if (a?.dueDate && !b?.dueDate) return -1;
      if (!a?.dueDate && b?.dueDate) return 1;

      // Finally, sort by creation date (newest first)
      const createdAtA = new Date(a.createdAt).getTime();
      const createdAtB = new Date(b.createdAt).getTime();
      return createdAtB - createdAtA;
    });
  }

  protected mapItem(item: ExtendedUITask): HomeItemType {
    // Ensure dates are Date objects, not strings
    const dueDate = item.dueDate ? new Date(item.dueDate) : undefined;
    const createdAt = item.createdAt ? new Date(item.createdAt) : new Date();
    const updatedAt = item.updatedAt ? new Date(item.updatedAt) : new Date();

    const date = dueDate || createdAt;
    const now = new Date();
    const isOverdue = dueDate && dueDate < now && item.status !== TaskStatus.DONE;

    let metadata = '';
    if (dueDate) {
      if (isOverdue) {
        metadata = 'Today';
      } else if (this.isDueToday(dueDate)) {
        metadata = plannedForToday;
      } else {
        metadata = `${formatLocalDateOnly(dueDate)}`;
      }
    } else if (item.status === TaskStatus.IN_PROGRESS) {
      const daysSinceCreation = Math.floor(
        (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceCreation === 0) {
        metadata = 'Started today';
      } else if (daysSinceCreation === 1) {
        metadata = 'Started yesterday';
      } else {
        metadata = `Started ${daysSinceCreation} days ago`;
      }
    } else {
      metadata = 'Not started';
    }

    return {
      id: item.id,
      title: item.title,
      time: formatTime(date),
      fullDate: formatFullDate(date),
      status: item.status,
      priority: item.priority,
      metadata,
      workspaceId: item.workspace?.id,
      transcriptId: item.transcript?.id,
      updatedAt,
      isFavorite: item.isFavorite
    };
  }

  public generateSectionsFromRawItems(items: ExtendedUITask[]): Section<ExtendedUITask>[] {
    const sections: {
      [plannedForToday]: ExtendedUITask[];
      'In Progress': ExtendedUITask[];
      'To Do': ExtendedUITask[];
    } = {
      [plannedForToday]: [],
      'In Progress': [],
      'To Do': []
    };

    const now = new Date();
    const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

    for (const item of items) {
      // Ensure updatedAt is a Date object
      const updatedAt = item.updatedAt ? new Date(item.updatedAt) : now;

      // Skip completed tasks that were updated more than 5 minutes ago
      if (item.status === TaskStatus.DONE && updatedAt < fiveMinutesAgo) {
        continue;
      }

      // Categorize by status and due date
      const dueDate = item.dueDate ? new Date(item.dueDate) : undefined;
      const isDueToday = dueDate && this.isDueToday(dueDate);

      if (isDueToday || (dueDate && dueDate < now)) {
        sections[plannedForToday].push(item);
      } else if (item.status === TaskStatus.IN_PROGRESS) {
        sections['In Progress'].push(item);
      } else {
        sections['To Do'].push(item);
      }
    }

    // Return sections in order, filtering empty ones
    const sectionOrder: Array<keyof typeof sections> = [plannedForToday, 'In Progress', 'To Do'];

    return sectionOrder
      .filter((section) => sections[section].length > 0)
      .map((title) => ({
        title,
        items: sections[title]
      }));
  }

  // Override to provide section metadata
  protected generateSectionsWithMetadata(items: ExtendedUITask[]): SectionWithMetadata<ExtendedUITask>[] {
    const sections = this.generateSectionsFromRawItems(items);

    const metadata: Record<string, SectionConfig> = {
      "Today's suggestions": {
        id: 'prioritized-today',
        icon: 'bolt',
        iconColor: 'text-orange-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 3
      },
      'In Progress': {
        id: 'in-progress',
        icon: 'chart_pie_fill' as any,
        iconColor: 'text-blue-500',
        defaultCollapsed: false,
        defaultDisplayLimit: 3
      },
      'To Do': {
        id: 'to-do',
        icon: 'checklist' as any,
        iconColor: 'text-slate-400',
        defaultCollapsed: false,
        defaultDisplayLimit: 3
      }
    };

    return sections.map(section => ({
      ...section,
      config: metadata[section.title]
    }));
  }

  protected generateSections(items: HomeItemType[]): Section<HomeItemType>[] {
    // This method is not used in the new architecture but kept for compatibility
    const sections: {
      [plannedForToday]: HomeItemType[];
      'In Progress': HomeItemType[];
      'To Do': HomeItemType[];
    } = {
      [plannedForToday]: [],
      'In Progress': [],
      'To Do': []
    };

    const now = new Date();
    const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

    for (const item of items) {
      // Ensure updatedAt is a Date object
      const updatedAt = item.updatedAt ? new Date(item.updatedAt) : now;

      // Skip completed tasks that were updated more than 5 minutes ago
      if (item.status === TaskStatus.DONE && updatedAt < fiveMinutesAgo) {
        continue;
      }

      // Categorize by status and due date
      const dueDate = item.fullDate ? new Date(item.fullDate) : null;
      const isDueToday = dueDate && this.isDueToday(dueDate);

      if (isDueToday || (dueDate && dueDate < now)) {
        sections[plannedForToday].push(item);
      } else if (item.status === TaskStatus.IN_PROGRESS) {
        sections['In Progress'].push(item);
      } else {
        sections['To Do'].push(item);
      }
    }

    // Return sections in order, filtering empty ones
    const sectionOrder: Array<keyof typeof sections> = [plannedForToday, 'In Progress', 'To Do'];

    return sectionOrder
      .filter((section) => sections[section].length > 0)
      .map((title) => ({
        title,
        items: sections[title]
      }));
  }

  private isDueToday(date: Date): boolean {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return false;
    }
    const now = new Date();
    return (
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate()
    );
  }

  protected getItemTags(item: UITask): string[] {
    const tags: string[] = [];

    if (item.priority) {
      tags.push(item.priority.toLowerCase());
    }

    if (item.status === TaskStatus.IN_PROGRESS) {
      tags.push('in-progress');
    }

    return tags;
  }

  protected getItemSpace(item: UITask): string | undefined {
    return item.workspace?.id;
  }

  protected getItemText(item: UITask): string {
    return `${item.title} ${item.label}`.trim();
  }

  protected getItemCalendar(_item: UITask): string | undefined {
    // Tasks don't have calendars
    return undefined;
  }

  protected getItemMemory(item: UITask): string | undefined {
    return item.transcript?.id;
  }

  protected isItemCompleted(item: UITask): boolean {
    return item.status === TaskStatus.DONE;
  }

  protected isItemDeleted(_item: UITask): boolean {
    return false; // Tasks don't have soft-delete in the current implementation
  }

  protected matchesDatePreset(item: UITask, datePresetValues: string[]): boolean {
    // If no date preset filter is applied, show all items
    if (datePresetValues.length === 0) {
      return true;
    }

    // Ensure dates are Date objects
    const dueDate = item.dueDate ? new Date(item.dueDate) : undefined;
    const createdAt = item.createdAt ? new Date(item.createdAt) : new Date();
    const date = dueDate || createdAt;
    const now = new Date();

    // Reset time to start of day for accurate day comparisons
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffTime = nowOnly.getTime() - dateOnly.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Check if the item matches any of the specified date preset values
    return datePresetValues.some((preset) => {
      switch (preset) {
        case 'future':
          return diffDays < 0;
        case 'today':
          return diffDays === 0;
        case 'this-week':
          return diffDays >= 0 && diffDays <= 7;
        case 'this-month':
          return diffDays >= 0 && diffDays <= 30;
        default:
          return true;
      }
    });
  }
}
